import type {
  LetterCoverageCell,
  LetterCoverageRow,
  LetterHiddenMix,
  LetterLibraryType,
  LetterNotNeededMix,
  LetterSampleCheck,
  LetterTemplateView,
  LetterVariant,
} from '@myrewards/contracts';
import type { IconName, StatusTone } from '../../ui';

/** Settings › Letters' words, in one place (APP-130). */
export const LETTER_TYPE_NAME: Readonly<Record<LetterLibraryType, string>> = {
  SDB: 'Sector Discretionary Bonus',
  EDB: 'Executive Discretionary Bonus',
  PAA: 'Professional Achievement Award',
  TAA: 'Team Achievement Award',
  SPOT: 'Spot Award',
  SBIR: 'SBIR',
  CRAD: 'CRAD',
};

/** Short names, for a heading that lists several types ("PAA, TAA and Spot"). */
export const LETTER_TYPE_SHORT: Readonly<Record<LetterLibraryType, string>> = {
  SDB: 'Sector bonus',
  EDB: 'Executive bonus',
  PAA: 'PAA',
  TAA: 'TAA',
  SPOT: 'Spot',
  SBIR: 'SBIR',
  CRAD: 'CRAD',
};

/** "A", "A and B", "A, B and C". */
function andList(words: readonly string[]): string {
  return words.length > 1
    ? `${words.slice(0, -1).join(', ')} and ${String(words.at(-1))}`
    : (words[0] ?? '');
}

/**
 * D101: who signs a group's letters, from its rows' source, for its heading. A named-person policy
 * names the person (D41). Null when the type has no single policy: the row says what to do.
 */
export function signedByWords(
  source: LetterCoverageRow['source'],
  person: string | null,
): string | null {
  switch (source) {
    case 'sector_lead':
      return 'signed by the awarding Sector Lead';
    case 'executive':
      return 'signed by the awarding Executive';
    case 'ceo':
      return 'signed by the CEO / President';
    case 'person':
      return `signed by ${person ?? 'a named person'}`;
    case null:
      return null;
  }
}

/** The CEO / President table's heading: the types in it, "PAA, TAA and Spot". */
export function ceoTableName(types: readonly LetterLibraryType[]): string {
  return andList(types.map((t) => LETTER_TYPE_SHORT[t]));
}

/** A cash-only type in a sentence: "a TAA", "a Spot Award". */
const A_TYPE: Readonly<Partial<Record<LetterLibraryType, string>>> = {
  TAA: 'a TAA',
  SPOT: 'a Spot Award',
};

/** D101: the text cell for a mix a type can't have, in the CEO table, saying why. */
export function notNeededWords(
  type: LetterLibraryType,
  because: LetterHiddenMix['because'],
): string {
  switch (because) {
    case 'cash_only_type':
      return `Not needed: ${A_TYPE[type] ?? `a ${LETTER_TYPE_NAME[type]}`} is paid in cash only.`;
    case 'paa_always_cash':
      return 'Not needed: at the cash share in force, a PAA always pays some cash.';
    case 'paa_never_options':
      return 'Not needed: at the cash share in force, a PAA is paid all in cash.';
    case 'paa_never_cash':
      return 'Not needed: at the cash share in force, a PAA pays no cash.';
  }
}

/**
 * D103 (the board's line, finished plainly): a grid with a row per Sector Lead or per Executive
 * grows by itself as people are named. Null when no grid has such rows.
 */
export function newSignerWords(has: { sectorLead: boolean; executive: boolean }): string | null {
  const who =
    has.sectorLead && has.executive
      ? 'Sector Lead or Executive'
      : has.sectorLead
        ? 'Sector Lead'
        : has.executive
          ? 'Executive'
          : null;
  return who === null
    ? null
    : `A new ${who} shows here as soon as they’re named, and their letters read Sample until each one is uploaded.`;
}

export const MIX_NAME: Readonly<Record<LetterVariant, string>> = {
  any: 'Any mix',
  cash_only: 'Cash only',
  options_only: 'Options only',
  cash_and_options: 'Cash and options',
};

/** "Sample letter · SDB v2": a version as people name it, by its design's own number. */
export function versionName(t: Pick<LetterTemplateView, 'name' | 'version' | 'designVersion'>) {
  return `${t.name} v${String(t.designVersion ?? t.version)}`;
}

export const vOf = (t: Pick<LetterTemplateView, 'version' | 'designVersion'>) =>
  `v${String(t.designVersion ?? t.version)}`;

/** What a cell reads, in words and with an icon, never by colour alone. */
export function cellWords(cell: LetterCoverageCell): {
  label: string;
  action: string | null;
  tone: StatusTone;
  icon: IconName;
} {
  switch (cell.state) {
    case 'ready':
      return {
        label: `Ready · ${cell.template ? vOf(cell.template) : ''}${cell.template?.variant === 'any' ? ', any mix' : ''}`,
        action: cell.waiting ? `Sample to review · ${vOf(cell.waiting)}` : null,
        tone: 'done',
        icon: 'check',
      };
    case 'sample_to_review':
      return {
        label: 'Sample to review',
        action: cell.template ? vOf(cell.template) : null,
        tone: 'waiting',
        icon: 'eye',
      };
    case 'approved':
      return {
        label: `Approved · ${cell.template ? vOf(cell.template) : ''}`,
        action: cell.waiting ? `Use it, or review ${vOf(cell.waiting)}` : 'Use it',
        tone: 'info',
        icon: 'check',
      };
    // APP-212: no real letter, so the built-in sample letter is made here. It isn't Ready.
    case 'sample':
      return cell.sentBack
        ? {
            label: 'Sample · sent back',
            action: 'Upload a corrected letter',
            tone: 'waiting',
            icon: 'file',
          }
        : { label: 'Sample', action: 'Upload the real letter', tone: 'waiting', icon: 'file' };
    case 'missing':
      return cell.sentBack
        ? {
            label: 'Sent back',
            action: 'Upload a corrected letter',
            tone: 'problem',
            icon: 'upload',
          }
        : { label: 'Missing', action: 'Upload', tone: 'problem', icon: 'upload' };
    // APP-221: a column that needs no letter, with none of its own. Not a gap, so no action.
    case 'not_needed':
      return { label: 'Not needed', action: null, tone: 'neutral', icon: 'minus' };
  }
}

/**
 * APP-221: why a column needs no letter, for the line under the grid and the cell's panel. The
 * fallback is said too, so nobody reads Not needed as "such an award gets no letter".
 */
const NOT_NEEDED_WORDS: Readonly<Record<LetterNotNeededMix['because'], string>> = {
  paa_always_options:
    'A PAA gives options as well as cash, so it doesn’t need a Cash only letter. If one ever pays cash only, its letter is made from MyRewards’ built-in sample letter.',
};
export function notNeededMixWords(notNeeded: LetterNotNeededMix): string {
  return NOT_NEEDED_WORDS[notNeeded.because];
}

/** APP-221: the line under a grid naming the columns that need no letter, and why. */
export function notNeededLineWords(notNeeded: readonly LetterNotNeededMix[]): string | null {
  const first = notNeeded[0];
  if (first === undefined) return null;
  return `Not needed: ${andList(notNeeded.map((n) => MIX_NAME[n.mix]))}. ${notNeededMixWords(first)}`;
}

/**
 * APP-212: what the open panel says about a cell the built-in sample fills, with the words the
 * sample letter itself prints, so nobody mistakes it for a real letter.
 */
export const BUILT_IN_SAMPLE_WORDS =
  'Letters here are made from MyRewards’ built-in sample letter, signed in the signer’s name and marked “Sample - Please have an admin configure in Settings”. Upload the real letter to replace it.';

/** Why a row can't have letters yet, in plain words. */
export function problemWords(row: LetterCoverageRow): string {
  const sector = row.sector?.name ?? 'This sector';
  if (row.problem === 'letter_policy_missing')
    return 'This award type has no rule for who signs. A System Admin sets one under Who signs each award type.';
  if (row.source === 'sector_lead')
    return row.problem === 'letter_signer_ambiguous'
      ? `${sector} has more than one Sector Lead, so no one can be picked to sign. Keep one in Settings › Roles & lists.`
      : `${sector} has no Sector Lead yet, so its letters have no one to sign them. A System Admin adds one in Settings › Roles & lists.`;
  if (row.source === 'executive')
    return 'There are no Executives on the list yet. A System Admin adds them in Settings › Roles & lists.';
  if (row.source === 'ceo') return 'No CEO / President is set yet. Choose one with Change above.';
  return 'The person who signs these letters isn’t set. A System Admin chooses them under Who signs each award type.';
}

/** The line under a grid: which mixes aren't shown, and why (D3). */
export function hiddenWords(type: LetterLibraryType, hidden: readonly LetterHiddenMix[]): string {
  const list = andList(hidden.map((h) => MIX_NAME[h.mix]));
  const because = hidden[0]?.because;
  const why =
    because === 'cash_only_type'
      ? `A ${LETTER_TYPE_NAME[type]} is paid in cash only, so its letters never show options.`
      : because === 'paa_always_cash'
        ? 'At the cash share in force, a PAA always pays some cash. Only an Executive’s change at approval can make one all options.'
        : because === 'paa_never_options'
          ? 'At the cash share in force, a PAA is paid all in cash.'
          : 'At the cash share in force, a PAA pays no cash.';
  return `Not shown: ${list}. ${why}`;
}

export const CHECK_RESULT: Readonly<
  Record<LetterSampleCheck['result'], { label: string; icon: IconName; tone: StatusTone }>
> = {
  passed: { label: 'Passed', icon: 'check', tone: 'done' },
  failed: { label: 'Failed', icon: 'alert', tone: 'problem' },
  warning: { label: 'Take a look', icon: 'info', tone: 'waiting' },
  not_run: { label: 'Not checked', icon: 'clock', tone: 'neutral' },
};

/**
 * Where the sample stands: none yet, made before checks were stored (D23), made before samples
 * showed the amounts the rules give and were marked SAMPLE (APP-202: no `sample_mark` check), or
 * checked. Only a checked sample can be approved.
 */
export function sampleState(
  t: LetterTemplateView,
): 'none' | 'predates_checks' | 'predates_mark' | 'checked' {
  if (t.checks) return t.checks.some((c) => c.id === 'sample_mark') ? 'checked' : 'predates_mark';
  return t.previewSha256 ? 'predates_checks' : 'none';
}

export function signsAs(role: LetterTemplateView['signerRole']): string {
  return role === 'president' ? ' (President)' : role === 'ceo' ? ' (CEO)' : '';
}
